import React, { useState, useEffect } from "react";
import { PhoneIcon, EnvelopeIcon, GlobeAltIcon } from "@heroicons/react/24/outline";
import Helper from "../helpers";

const VenueInformation = () => {
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shouldDisplay, setShouldDisplay] = useState(false);

    useEffect(() => {
        const fetchVenue = async () => {
            try {
                setLoading(true);
                setVenue(null)
                // Get the current user's venue ID/key
                const currentUserVenueId = await Helper.getUserVenueId();
                
                if (!currentUserVenueId) {
                    // User doesn't have a venue associated
                    setShouldDisplay(false);
                    setLoading(false);
                    return;
                }
                
            } catch (err) {
                console.error('Error loading venue information:', err);
                setError('Failed to load venue information');
                setShouldDisplay(false);
            } finally {
                setLoading(false);
            }
        };

        fetchVenue();
    }, []);

    if (loading) {
        return (
            <div className="backdrop-blur-sm bg-gray-900/20 rounded-xl p-6 mb-4 animate-pulse">
                <div className="flex items-center space-x-6">
                    <div className="bg-gray-700/50 rounded-xl w-48 h-32"></div>
                    <div className="flex-1 space-y-3">
                        <div className="bg-gray-700/50 h-6 rounded w-1/3"></div>
                        <div className="bg-gray-700/50 h-4 rounded w-1/2"></div>
                        <div className="bg-gray-700/50 h-4 rounded w-2/3"></div>
                        <div className="bg-gray-700/50 h-4 rounded w-1/2"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Don't display anything if venue doesn't match or there's no venue
    if (!shouldDisplay || !venue) {
        return null;
    }

    if (error) {
        return (
            <div className="backdrop-blur-sm bg-red-900/20 border border-red-800/30 rounded-xl p-6 mb-4 mt-6">
                <p className="text-red-300">{error}</p>
            </div>
        );
    }

    return (
        <div className="backdrop-blur-sm bg-gray-900/20 border border-gray-700/30 rounded-xl p-6 mb-4 mt-6 hover:bg-gray-900/30 transition-all duration-300">
            <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
                {/* Logo */}
                <div className="flex-shrink-0">
                    <div className="bg-gray-800/50 rounded-xl p-4 w-full lg:w-48 h-32 flex items-center justify-center overflow-hidden">
                        <img 
                            src={venue.logoUrl} 
                            alt={`${venue.name} Logo`}
                            className="w-full h-full object-cover object-center rounded-lg"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="hidden w-full h-full items-center justify-center bg-gray-700/50 rounded-lg">
                            <span className="text-gray-400 text-sm">{venue.name}</span>
                        </div>
                    </div>
                </div>

                {/* Venue Details */}
                <div className="flex-1 w-full lg:w-auto">
                    <h2 className="text-2xl font-bold text-white mb-4">{venue.name}</h2>
                    
                    <div className="space-y-2">
                        {venue.phone && (
                            <div className="flex items-center space-x-3 text-gray-300">
                                <PhoneIcon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                                <span className="text-sm">{venue.phone}</span>
                            </div>
                        )}
                        
                        {venue.email && (
                            <div className="flex items-center space-x-3 text-gray-300">
                                <EnvelopeIcon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                                <span className="text-sm">{venue.email}</span>
                            </div>
                        )}
                        
                        {venue.website && (
                            <div className="flex items-center space-x-3 text-gray-300">
                                <GlobeAltIcon className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                                <a 
                                    href={venue.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-emerald-400 hover:text-emerald-300 underline transition-colors duration-200"
                                >
                                    {venue.websiteDisplay || venue.website}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VenueInformation;
